// Dates and times in English (UK) style, matching the design: "Fri 16 Oct", "Mon 19 Oct 09:00", "Yest. 17:46".
// Numbers and money come from the shared formatters so the server writes them the same way.

import { shortMonth } from '@shared/format';

export { formatAtIn, formatBytes, formatDecimal, formatMoney, formatMoneyShort, formatNumber, formatUsd, initials, plural, shortUrl } from '@shared/format';

type DateInput = Date | string | number;

const DAY_MS = 86_400_000;
const weekdayFmt = new Intl.DateTimeFormat('en-GB', { weekday: 'short' });
const weekdayLongFmt = new Intl.DateTimeFormat('en-GB', { weekday: 'long' });
const dayMonthIntl = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const dayMonthYearIntl = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const dayMonthFmt = { format: (d: Date) => shortMonth(dayMonthIntl.format(d)) };
const dayMonthYearFmt = { format: (d: Date) => shortMonth(dayMonthYearIntl.format(d)) };
const longDateFmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });

export function toDate(d: DateInput): Date {
  return d instanceof Date ? d : new Date(d);
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Whole calendar days from today to the date: 0 today, 1 tomorrow, -1 yesterday. */
export function daysFromToday(d: DateInput, now = new Date()): number {
  return Math.round((startOfDay(toDate(d)) - startOfDay(now)) / DAY_MS);
}

/** "09:04" */
export function formatTime(d: DateInput): string {
  return timeFmt.format(toDate(d));
}

/** "Fri 16 Oct", with the year added when it is not this year: "Fri 16 Oct 2025". */
export function formatDay(d: DateInput): string {
  const date = toDate(d);
  const base = `${weekdayFmt.format(date)} ${dayMonthFmt.format(date)}`;
  return date.getFullYear() === new Date().getFullYear() ? base : `${base} ${date.getFullYear()}`;
}

/** "16 Oct", or "16 Oct 2025" outside this year. */
export function formatDate(d: DateInput): string {
  const date = toDate(d);
  return date.getFullYear() === new Date().getFullYear() ? dayMonthFmt.format(date) : dayMonthYearFmt.format(date);
}

/** "16 October 2026" for formal places such as backups and invitations. */
export function formatLongDate(d: DateInput): string {
  return longDateFmt.format(toDate(d));
}

/** "Mon 19 Oct 09:00" */
export function formatDayTime(d: DateInput): string {
  return `${formatDay(d)} ${formatTime(d)}`;
}

/**
 * A compact moment for lists and feeds: "09:04" today, "Yest. 17:46", "Tue 14:20" within the past week,
 * "12 Oct" earlier this year, "12 Oct 2025" before that. Future moments read "Tomorrow 09:00" or "Mon 19 Oct 09:00".
 */
export function formatWhen(d: DateInput): string {
  const date = toDate(d);
  const days = daysFromToday(date);
  if (days === 0) return formatTime(date);
  if (days === -1) return `Yest. ${formatTime(date)}`;
  if (days === 1) return `Tomorrow ${formatTime(date)}`;
  if (days > 1) return formatDayTime(date);
  if (days > -7) return `${weekdayFmt.format(date)} ${formatTime(date)}`;
  return formatDate(date);
}

/** "Just now", "5 min ago", "3 h ago", "2 days ago", "16 Oct". */
export function formatAgo(d: DateInput, now = new Date()): string {
  const date = toDate(d);
  const mins = Math.round((now.getTime() - date.getTime()) / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24 && daysFromToday(date, now) === 0) return `${hours} h ago`;
  const days = -daysFromToday(date, now);
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return formatDate(date);
}

/** "Today", "Yesterday", "Tomorrow", then "Wednesday 14 Oct" for grouping feeds by day. */
export function dayLabel(d: DateInput): string {
  const date = toDate(d);
  const days = daysFromToday(date);
  if (days === 0) return 'Today';
  if (days === -1) return 'Yesterday';
  if (days === 1) return 'Tomorrow';
  const base = `${weekdayLongFmt.format(date)} ${dayMonthFmt.format(date)}`;
  return date.getFullYear() === new Date().getFullYear() ? base : `${base} ${date.getFullYear()}`;
}

/** "in 3 days", "today", "2 days overdue" for due dates. */
export function formatDue(d: DateInput): string {
  const days = daysFromToday(d);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days > 1) return `in ${days} days`;
  if (days === -1) return '1 day overdue';
  return `${-days} days overdue`;
}
